import * as PIXI from "pixi.js";
import {Callback} from "./Reflection";
import {ISignal, Signal} from "./Signal";

export interface ISpineAnimation {
    animate(animationName: string, duration?: number | Callback | boolean, cb?: Callback): PIXI.spine.core.TrackEntry;
    getCurrentAnimation(): PIXI.spine.core.TrackEntry;
}

export class Spine extends PIXI.spine.Spine implements ISpineAnimation {

    get onAnimationComplete(): ISignal<(track: PIXI.spine.core.TrackEntry) => void> {
        this.addSpineListeners();
        return this._onAnimationComplete;
    }

    get onAnimationEvent(): ISignal<(track: PIXI.spine.core.TrackEntry, event: PIXI.spine.core.Event) => void> {
        this.addSpineListeners();
        return this._onAnimationEvent;
    }

    private _onAnimationComplete!: ISignal<(track: PIXI.spine.core.TrackEntry) => void>;
    private _onAnimationEvent!: ISignal<(track: PIXI.spine.core.TrackEntry, event: PIXI.spine.core.Event) => void>;

    private readonly _tracks: SpineTrack[] = [];

    constructor(skeletonData: PIXI.spine.core.SkeletonData) {
        super(skeletonData);

        this.autoUpdate = false;
    }

    public animate(animationName: string, a?: any, b?: any): PIXI.spine.core.TrackEntry {
        return this.track(0).animate(animationName, a, b);
    }

    public getCurrentAnimation(): PIXI.spine.core.TrackEntry {
        return this.state.getCurrent(0);
    }

    public track(index: number): ISpineAnimation {
        if (!this._tracks[index]) {
            this._tracks[index] = new SpineTrack(this, index);
        }
        return this._tracks[index];
    }

    public setMixes(mix: number, from?: string | RegExp, to?: string | RegExp): void {
        const fromString = typeof from === "string" ? from : null;
        const toString = typeof to === "string" ? to : null;
        if (fromString && toString) {
            this.stateData.setMix(fromString, toString, mix);
        } else {
            const fromRegExp = from instanceof RegExp ? from : null;
            const toRegExp = to instanceof RegExp ? to : null;
            const isFrom = fromString || fromRegExp;
            const isTo = toString || toRegExp;
            const testFrom = (name: string) => {
                return (fromString && name === fromString) || (fromRegExp && fromRegExp.test(name));
            };
            const testTo = (name: string) => {
                return (toString && name === toString) || (toRegExp && toRegExp.test(name));
            };
            const names = this.spineData.animations.map((it) => it.name);
            for (const fromName of names) {
                for (const toName of names) {
                    if (fromName !== toName && (
                        (!isFrom && !isTo) ||
                        (isFrom && isTo && testFrom(fromName) && testTo(toName)) ||
                        (isFrom && !isTo && testFrom(fromName)) ||
                        (!isFrom && isTo && testTo(toName)))) {
                        this.stateData.setMix(fromName, toName, mix);
                    }
                }
            }
        }
    }

    public getDuration(...names: string[]): number {
        return this.spineData.animations
            .filter((it) => names.indexOf(it.name) > 1)
            .reduce((sum, it) => sum + it.duration, 0);
    }

    public destroy(): void {
        if (this._onAnimationComplete) {
            this._onAnimationComplete.remove();
        }
        if (this._onAnimationEvent) {
            this._onAnimationEvent.remove();
        }
        super.destroy({children: true});
    }

    private addSpineListeners(): void {
        if (!this._onAnimationComplete) {
            this._onAnimationComplete = Signal<(track: PIXI.spine.core.TrackEntry) => void>();
            this._onAnimationEvent = Signal<(track: PIXI.spine.core.TrackEntry, event: PIXI.spine.core.Event) => void>();
            this.state.addListener({
                complete: this._onAnimationComplete,
                event: this._onAnimationEvent,
            });
        }
    }
}

export class SpineTrack implements ISpineAnimation {

    private _currentLoop?: PIXI.spine.core.TrackEntry;
    private readonly spine: Spine;
    private readonly index: number;

    constructor(spine: Spine, index: number) {
        this.spine = spine;
        this.index = index;
    }

    public animate(animationName: string, a?: any, b?: any): PIXI.spine.core.TrackEntry {

        let track: PIXI.spine.core.TrackEntry;
        if (typeof a === "boolean") {

            if (a) {
                if (!this._currentLoop) {
                    this._currentLoop = track = this.spine.state.setAnimation(this.index, animationName, true);
                } else if (this._currentLoop.animation.name !== animationName) {
                    this._currentLoop = track = this.spine.state.setAnimation(this.index, animationName, true);
                } else {
                    track = this._currentLoop;
                }
            } else {
                this._currentLoop = undefined;
                track = this.spine.state.setAnimation(this.index, animationName, false);
            }

        } else if (typeof a === "number") {

            this._currentLoop = undefined;
            track = this.spine.state.setAnimation(this.index, animationName, false);
            track.timeScale = track.animation.duration / a;

            if (typeof b === "function") {
                this.callOnComplete(animationName, b);
            }

        } else if (typeof a === "function") {

            this._currentLoop = undefined;
            track = this.spine.state.setAnimation(this.index, animationName, false);
            this.callOnComplete(animationName, a);

        } else {

            this._currentLoop = undefined;
            track = this.spine.state.setAnimation(this.index, animationName, false);
        }

        this.spine.update(0);
        return track;
    }

    public getCurrentAnimation(): PIXI.spine.core.TrackEntry {
        return this.spine.state.getCurrent(this.index);
    }

    private callOnComplete(animationName: string, listener: Callback) {
        const onComplete = (track: PIXI.spine.core.TrackEntry) => {
            const current = this.spine.state.getCurrent(this.index);
            if (track.trackIndex === this.index &&
                track.animation.name === animationName &&
                current !== null && current.animation.name === animationName) {
                this.spine.onAnimationComplete.remove(onComplete);
                listener();
            }
        };
        this.spine.onAnimationComplete.add(onComplete);
    }
}
